/**
 * Purchase Recording Page
 * Allows recording ingredient purchases and stock adjustments
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { inventoryApi } from '../lib/api/inventory';
import type { Ingredient, PurchaseRequest } from '../types/inventory';

export default function PurchaseRecordingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [selectedIngredient, setSelectedIngredient] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<PurchaseRequest>({
    quantity: 0,
    unitCost: undefined,
    supplierOrderId: undefined,
  });

  useEffect(() => {
    loadIngredients();
  }, []);

  const loadIngredients = async () => {
    setLoading(true);
    try {
      const data = await inventoryApi.getIngredients(true); // Active only
      setIngredients(data);
    } catch (error) {
      console.error('Failed to load ingredients:', error);
      alert('Errore nel caricamento degli ingredienti');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedIngredient) {
      alert('Seleziona un ingrediente');
      return;
    }

    if (formData.quantity <= 0) {
      alert('La quantità deve essere maggiore di zero');
      return;
    }

    setSubmitting(true);
    try {
      await inventoryApi.recordPurchase(selectedIngredient, formData);
      alert('Acquisto registrato con successo!');
      navigate('/dashboard/inventory/ingredients');
    } catch (error) {
      console.error('Failed to record purchase:', error);
      alert('Errore nella registrazione dell\'acquisto');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredIngredients = ingredients.filter(
    (ing) =>
      ing.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ing.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedIngredientData = ingredients.find((ing) => ing.id === selectedIngredient);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-400">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 transition-colors duration-300">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl border-b border-gray-200 dark:border-slate-700/50 shadow-sm dark:shadow-xl dark:shadow-slate-950/50">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white dark:drop-shadow-[0_2px_10px_rgba(255,255,255,0.15)]">📥 Registra Acquisto</h1>
              <p className="text-gray-600 dark:text-slate-400 mt-1">Registra un nuovo acquisto di ingredienti</p>
            </div>
            <Link
              to="/dashboard/inventory/ingredients"
              className="px-4 py-2 text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
            >
              Annulla
            </Link>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-sm dark:shadow-xl dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-700/50 dark:ring-1 dark:ring-white/5 p-6 transition-all duration-300"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Ingredient Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Ingrediente *
              </label>

              {/* Search Bar */}
              <div className="relative mb-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca ingrediente..."
                  className="w-full px-4 py-2 pl-10 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {/* Ingredient Select */}
              <select
                value={selectedIngredient}
                onChange={(e) => setSelectedIngredient(e.target.value)}
                required
                className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
              >
                <option value="">Seleziona ingrediente...</option>
                {filteredIngredients.map((ingredient) => (
                  <option key={ingredient.id} value={ingredient.id}>
                    {ingredient.name} ({ingredient.currentStock} {ingredient.unitOfMeasure})
                  </option>
                ))}
              </select>

              {/* Selected Ingredient Info */}
              {selectedIngredientData && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 dark:ring-1 dark:ring-blue-500/20 rounded-lg"
                >
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-slate-400">Scorta attuale:</span>
                      <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                        {selectedIngredientData.currentStock} {selectedIngredientData.unitOfMeasure}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-slate-400">Scorta minima:</span>
                      <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                        {selectedIngredientData.minimumStock} {selectedIngredientData.unitOfMeasure}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-slate-400">Costo unitario:</span>
                      <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                        €{selectedIngredientData.unitCost.toFixed(2)}
                      </span>
                    </div>
                    {selectedIngredientData.supplierName && (
                      <div>
                        <span className="text-gray-600 dark:text-slate-400">Fornitore:</span>
                        <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                          {selectedIngredientData.supplierName}
                        </span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Quantità *
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={formData.quantity || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })
                  }
                  required
                  className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
                  placeholder="0.00"
                />
                {selectedIngredientData && (
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-slate-400">
                    {selectedIngredientData.unitOfMeasure}
                  </span>
                )}
              </div>
            </div>

            {/* Unit Cost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Costo Unitario (opzionale)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-slate-400">
                  €
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.unitCost || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      unitCost: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
                  placeholder={selectedIngredientData?.unitCost.toFixed(2) || '0.00'}
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                Se non specificato, verrà usato il costo unitario dell'ingrediente
              </p>
            </div>

            {/* Supplier Order ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                ID Ordine Fornitore (opzionale)
              </label>
              <input
                type="text"
                value={formData.supplierOrderId || ''}
                onChange={(e) =>
                  setFormData({ ...formData, supplierOrderId: e.target.value || undefined })
                }
                className="w-full px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
                placeholder="es. ORD-2024-001"
              />
            </div>

            {/* Total Cost Preview */}
            {selectedIngredientData && formData.quantity > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/50 dark:ring-1 dark:ring-green-500/20 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-400">Costo Totale Stimato</p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                      €
                      {(
                        formData.quantity *
                        (formData.unitCost || selectedIngredientData.unitCost)
                      ).toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-slate-400">Nuova Scorta</p>
                    <p className="text-xl font-semibold text-gray-900 dark:text-white">
                      {(selectedIngredientData.currentStock + formData.quantity).toFixed(2)}{' '}
                      {selectedIngredientData.unitOfMeasure}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Submit Buttons */}
            <div className="flex items-center gap-4 pt-6 border-t border-gray-200 dark:border-slate-700/50">
              <button
                type="submit"
                disabled={submitting || !selectedIngredient}
                className="flex-1 px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 shadow-lg dark:shadow-indigo-900/50 transition-all duration-200 disabled:bg-gray-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed font-semibold"
              >
                {submitting ? 'Registrazione...' : '📥 Registra Acquisto'}
              </button>
              <Link
                to="/dashboard/inventory/ingredients"
                className="px-6 py-3 text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                Annulla
              </Link>
            </div>
          </form>
        </motion.div>

        {/* Help Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 dark:ring-1 dark:ring-blue-500/20 rounded-lg"
        >
          <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">💡 Suggerimenti</h3>
          <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
            <li>• La quantità verrà automaticamente aggiunta alla scorta attuale</li>
            <li>• Il costo unitario è opzionale, se omesso verrà usato quello dell'ingrediente</li>
            <li>• Inserisci l'ID ordine fornitore per tracciare meglio gli acquisti</li>
            <li>• Questa operazione creerà un movimento di tipo "ACQUISTO" nello storico</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
}
